import {
  createContext,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { ScrollView } from 'react-native';
import { getSelectedTexts, getTexts } from '@/api/services/textset';
import { getTags, TagResponse } from '@/api/services/wordset';
import { CHANGE_PAGE, OUTER_STATE } from './constants';
import { logError } from '@/utils/logHandling';
import {
  ActionType,
  StateType,
  TextItem,
  TextsetContextValueType,
  TextsetData,
  TextsetEditData,
} from './types';

export const TextsetContext = createContext<TextsetContextValueType>(
  {} as TextsetContextValueType,
);

const DEFAULT_VALUE: StateType = {
  term: '',
  termType: 'text',
  complexity: 'einfach',
  textLength: 'kurz',
  searchTextsPerPage: 30,
  page: 1,
};

const reducer = (state: StateType, action: ActionType): StateType => {
  const { type, payload } = action;
  switch (type) {
    case OUTER_STATE: {
      const { name, value } = payload;
      return {
        ...state,
        page: 1,
        [name]: value,
      };
    }
    case CHANGE_PAGE: {
      const { value, arrowName } = payload;
      if (arrowName === null) {
        return {
          ...state,
          page: value,
        };
      } else {
        if (arrowName === 'right') {
          return {
            ...state,
            page: state.page + 1,
          };
        } else {
          return {
            ...state,
            page: state.page - 1,
          };
        }
      }
    }
    default: {
      return state;
    }
  }
};

interface TextsetContextProviderProps {
  children: React.ReactNode;
  patientId?: string;
  setId?: string;
  apiName?: string;
}

export function TextsetContextProvider({
  children,
  patientId,
  setId,
  apiName,
}: TextsetContextProviderProps) {
  const [state, dispatch] = useReducer(reducer, DEFAULT_VALUE);
  const scrollViewRef = useRef<ScrollView | null>(null);

  const [data, setData] = useState<TextsetData>({
    texts: [],
    totalCount: 0,
  });
  const [tags, setTags] = useState<{
    texts: { displayHierarchy: string }[];
  } | null>(null);
  const [limitModal, setLimitModal] = useState(false);
  const [selectedData, setSelectedData] = useState<TextItem[]>([]);
  const componentMounted = useRef<boolean>(false);
  const requestRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const editData = useRef<TextsetEditData | null>(null);

  const handleOuterState = useCallback(
    (name: keyof StateType, value: unknown) => {
      dispatch({
        type: OUTER_STATE,
        payload: { name, value },
      });
    },
    [],
  );

  const changePage = useCallback(
    (value: number, arrowName: 'right' | 'left' | null = null) => {
      dispatch({
        type: CHANGE_PAGE,
        payload: { value, arrowName },
      });
      if (scrollViewRef.current && value) {
        scrollViewRef.current.scrollTo({ y: 300, animated: true });
      }
    },
    [],
  );

  const changeLimitModal = useCallback(() => {
    setLimitModal((prevState) => !prevState);
  }, []);

  const changeSelectedData = useCallback((newSelectedData: TextItem[]) => {
    setSelectedData(newSelectedData);
  }, []);

  const changeData = useCallback((newData: TextsetData) => {
    setData(newData);
  }, []);

  const handleFetchTexts = useCallback(async (object: StateType) => {
    try {
      let payload;
      if (object?.termType === 'text' || object?.termType === 'tag') {
        payload = { ...object, term: object?.term.trim() };
      } else {
        payload = { ...object };
      }
      const { textLength, ...rest } = payload;
      const { data } = await getTexts(rest);
      const textsWithLength = data.texts.map((text) => {
        return {
          ...text,
          textLength,
        };
      });
      data.texts = textsWithLength;
      setData(data);
    } catch (error) {
      logError('Could not fetch texts', error);
    }
  }, []);

  const handleFetchSelectedTexts = useCallback(
    async (
      apiName: string | undefined,
      patientId: string | undefined,
      setId: string | undefined,
    ) => {
      try {
        const { data } = await getSelectedTexts(apiName, patientId, setId);
        editData.current = data;
        const { texts } = data;
        setSelectedData(texts);
      } catch (error) {
        logError('Could not fetch selected texts', error);
      }
    },
    [],
  );

  const handleFetchTags = useCallback(async () => {
    try {
      const { data } = await getTags();
      const { texts } = data as TagResponse;
      setTags({ texts });
    } catch (error) {
      logError('Could not fetch tags', error);
    }
  }, []);

  const refetchTexts = useCallback(() => {
    handleFetchTexts(state);
  }, [state]);

  useEffect(() => {
    if (!componentMounted.current) {
      return;
    }
    handleFetchTexts(state);
  }, [state.page, state.termType, state.complexity, state.textLength]);

  useEffect(() => {
    if (!componentMounted.current) {
      return;
    }
    clearTimeout(requestRef.current!);
    requestRef.current = setTimeout(() => {
      handleFetchTexts(state);
    }, 500);
  }, [state.term]);

  useEffect(() => {
    const fetchTexts = async () => {
      if (!setId) {
        handleFetchTexts(state);
        handleFetchTags();
      } else {
        try {
          handleFetchTags();
          handleFetchTexts(state);
          handleFetchSelectedTexts(apiName, patientId, setId);
        } catch (error) {
          logError('Could not fetch ', error);
        }
      }
      componentMounted.current = true;
    };
    fetchTexts();
  }, []);

  const value: TextsetContextValueType = {
    data,
    selectedData,
    limitModal,
    editData: editData.current,
    page: state.page,
    complexity: state.complexity,
    searchTextsPerPage: state.searchTextsPerPage,
    term: state.term,
    termType: state.termType,
    textLength: state.textLength,
    handleOuterState,
    changeData,
    changeSelectedData,
    changePage,
    changeLimitModal,
    refetchTexts,
    patientId,
    setId,
    apiName,
    scrollViewRef,
    tags:
      tags?.texts?.map((item) => {
        return item.displayHierarchy;
      }) ?? [],
  };

  return (
    <TextsetContext.Provider value={value}>{children}</TextsetContext.Provider>
  );
}
